import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './CurrentNextLoadCalc.translations.json';
import React, { FC, useMemo } from 'react';
import { DashboardPanel } from '../DashboardPanel';
import { Table } from '../../common/tanstacktable';
import styled from 'styled-components';
import { useListCurrentNextLoadCalcQuery } from './query/useGetCurrentNextLoadCalcQuery';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { CurrentNextLoadCalcType } from './schema/types';
import { Flex } from 'antd';
import { colorGreen, colorYellow } from '@atlas-frontend/design-tokens';
import { formatDateTimeShort } from '../../common/utils';
import { InfoIconUrl, TextEllipsis } from '../../common/components';
import { GroupedValueDates } from '../GroupedValueDates';

const { addTranslations, t } = i18nNsFactory('current-next-load-calc');
addTranslations(translations);

const getColor = (code: string | null) => {
  switch (code) {
    case 'PROCESSING':
      return colorGreen[2];
    default:
      return colorYellow[1];
  }
};

const columnHelper = createColumnHelper<CurrentNextLoadCalcType>();
function getColumns(): ColumnDef<CurrentNextLoadCalcType, any>[] {
  const columns: ColumnDef<CurrentNextLoadCalcType, any>[] = [];
  columns.push(
    columnHelper.accessor('name', {
      header: t(`name.label`),
      cell: (props) => {
        return (
          <TextEllipsis
            tooltip={<CurrentNextLoadCalcTooltip item={props.row.original} />}
            renderTooltip={'always'}
          >
            {props.renderValue()}
          </TextEllipsis>
        );
      },
    }),
    columnHelper.accessor('valueDates', {
      header: t(`valueDates.label`),
      enableColumnFilter: false,
      cell: (props) => {
        const row = props.row.original;
        return (
          <TextEllipsis
            tooltip={<CurrentNextLoadCalcTooltip item={row} />}
            renderTooltip={'always'}
          >
            <CurrentNextLoadCalcValueDates item={row} separator={', '} />
          </TextEllipsis>
        );
      },
    }),
    columnHelper.accessor('processingState', {
      header: t(`processingState.label`),
      cell: (props) => {
        const label = t(`processingState.` + props.renderValue());
        return (
          <Flex align="center" gap={6}>
            <Oval $fill={getColor(props.renderValue())}>{label}</Oval>
          </Flex>
        );
      },
      filterFn: 'includesOption',
    }),
    columnHelper.accessor('processingTime', {
      header: t(`processingTime.label`),
      cell: (props) => formatDateTimeShort(props.renderValue()) ?? '',
      enableColumnFilter: false,
    }),
  );

  return columns;
}

export const CurrentNextLoadCalc = () => {
  const { data: currentNextLoadCalc = [], isFetching } =
    useListCurrentNextLoadCalcQuery();

  const columns: ColumnDef<CurrentNextLoadCalcType, any>[] = useMemo(
    () => getColumns(),
    [],
  );

  const options = useMemo(
    () => ({
      processingState: [
        {
          id: 'PROCESSING',
          name: t('processingState.PROCESSING'),
        },
        {
          id: 'null',
          name: t('processingState.NEXT'),
        },
      ],
    }),
    [],
  );

  return (
    <DashboardPanel
      title={t('title')}
      infoIconUrl={InfoIconUrl.CURRENT_NEXT_LOAD_CALC}
      hideEmptyImage
    >
      <Table
        name="user-followed-data-object-group-table"
        columns={columns}
        data={currentNextLoadCalc}
        hideClearFilters
        isLoading={isFetching}
        columnOrderingDisabled={true}
        columnResizeDisabled={true}
        options={options}
        hideEmptyImage
        sticky
        virtual
        scrollContainerSelector="#dashboard-panel-content"
      />
    </DashboardPanel>
  );
};

export const CurrentNextLoadCalcValueDates: FC<{
  item: CurrentNextLoadCalcType;
  separator: React.ReactNode;
}> = ({ item, separator }) => {
  if (item.periodLoadingType === 'ALL') {
    return t('valueDates.ALL');
  } else if (item.valueDatesLabel) {
    return item.valueDatesLabel;
  } else {
    return (
      <GroupedValueDates
        frequency={item.frequency}
        valueDates={item.valueDates}
        separator={separator}
      />
    );
  }
};

export const CurrentNextLoadCalcTooltip: FC<{
  item: CurrentNextLoadCalcType;
}> = ({ item }) => {
  return (
    <>
      <strong>{item.name}</strong>
      <div>
        {' '}
        <CurrentNextLoadCalcValueDates
          item={item}
          separator={
            <>
              ,<br />
            </>
          }
        />
      </div>
    </>
  );
};

const Oval = styled.div<{ $fill: string }>`
  background-color: ${(props) => props.$fill};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: 18px;
  padding: 8px;
`;
